/**
 * DayScoreHeader - Date navigation + composite day score
 */

import React from 'react';
import { format, addDays, subDays, isToday } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Button } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface DayScoreHeaderProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  dayScore: number | null;
  loading?: boolean;
}

const DayScoreHeader: React.FC<DayScoreHeaderProps> = ({
  selectedDate,
  onDateChange,
  dayScore,
  loading = false,
}) => {
  const getScoreColor = (score: number | null): string => {
    if (score === null) return COLORS.text.tertiary;
    if (score >= 75) return COLORS.status.success;
    if (score >= 50) return COLORS.status.warning;
    return COLORS.status.error;
  };

  const canGoForward = !isToday(selectedDate);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: SPACING[12],
        marginBottom: SPACING[16],
      }}
    >
      {/* Date Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
        <button
          onClick={() => onDateChange(subDays(selectedDate, 1))}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: RADIUS,
            border: `1px solid ${COLORS.border.default}`,
            backgroundColor: COLORS.bg.panel,
            cursor: 'pointer',
          }}
        >
          <ChevronLeftIcon style={{ width: '14px', height: '14px', color: COLORS.text.primary }} />
        </button>

        <span
          style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h2,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            minWidth: '140px',
            textAlign: 'center',
          }}
        >
          {format(selectedDate, 'MMM d, yyyy')}
        </span>

        <button
          onClick={() => canGoForward && onDateChange(addDays(selectedDate, 1))}
          disabled={!canGoForward}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: RADIUS,
            border: `1px solid ${COLORS.border.default}`,
            backgroundColor: COLORS.bg.panel,
            cursor: canGoForward ? 'pointer' : 'not-allowed',
            opacity: canGoForward ? 1 : 0.4,
          }}
        >
          <ChevronRightIcon style={{ width: '14px', height: '14px', color: COLORS.text.primary }} />
        </button>

        {!isToday(selectedDate) && (
          <Button variant="ghost" size="sm" onClick={() => onDateChange(new Date())}>
            Today
          </Button>
        )}
      </div>

      {/* Day Score */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
        <span style={{ fontSize: TYPOGRAPHY.body.sizes.default, color: COLORS.text.secondary }}>
          Day Score
        </span>
        {loading ? (
          <span style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, color: COLORS.text.tertiary }}>...</span>
        ) : dayScore !== null ? (
          <span
            style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h1,
              fontWeight: '700',
              color: getScoreColor(dayScore),
            }}
          >
            {dayScore}
          </span>
        ) : (
          <span style={{ fontSize: TYPOGRAPHY.body.sizes.default, color: COLORS.text.tertiary }}>-</span>
        )}
      </div>
    </div>
  );
};

export default DayScoreHeader;
